import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, XCircle, Phone, Mail, Package, ArrowRight, AlertTriangle } from 'lucide-react';

const GOLD = '#D4AF37';
const INK = '#0F0F0F';
const MUTED = '#595959';
const LINE = '#E5E5E5';
const SOFT = '#FAF9F6';

const PaymentResult = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const { success, orderId, reason, totalPrice, advanceAmount, balanceAmount, notifications } = state;
  
  // If no state passed (direct URL access), show a fallback
  if (!location.state) {
    return (
      <div data-testid="payment-result-no-state" className="min-h-screen flex flex-col items-center justify-center px-6" style={{ backgroundColor: SOFT, fontFamily: 'Outfit, sans-serif' }}>
        <AlertTriangle className="w-12 h-12 mb-4" style={{ color: GOLD }} strokeWidth={1.5} />
        <h1 className="text-xl mb-2" style={{ fontFamily: 'Playfair Display, serif', color: INK }}>No Payment Data</h1>
        <p className="text-sm text-center mb-6" style={{ color: MUTED }}>
          This page requires payment information. Please complete checkout or check your orders.
        </p>
        <div className="space-y-2 w-full max-w-xs">
          <button onClick={() => navigate('/my-orders')}
            className="w-full py-3 text-[11px] tracking-[0.08em] uppercase text-white"
            style={{ backgroundColor: INK }}>
            View My Orders
          </button>
          <button onClick={() => navigate('/landing')}
            className="w-full py-3 text-[10px] tracking-[0.08em] uppercase border" style={{ borderColor: LINE, color: MUTED }}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    const emailSent = notifications?.email?.sent;
    const smsSent = notifications?.sms?.sent;
    return (
      <div data-testid="payment-result-success" className="min-h-screen flex flex-col" style={{ backgroundColor: SOFT, fontFamily: 'Outfit, sans-serif' }}>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <div className="relative mb-5">
            <div className="w-24 h-24 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #166534 0%, #0F4827 100%)' }}>
              <CheckCircle className="w-12 h-12 text-white" strokeWidth={1.5} />
            </div>
            <div className="absolute -inset-2 rounded-full opacity-30 animate-ping" style={{ backgroundColor: '#166534' }} />
          </div>
          <p className="text-[10px] tracking-[0.2em] uppercase mb-2" style={{ color: GOLD }}>Payment Successful</p>
          <h1 className="text-[26px] leading-tight mb-1" style={{ fontFamily: 'Playfair Display, serif', color: INK }}>
            Order Confirmed
          </h1>
          <p className="text-[12px] mb-4" style={{ color: MUTED }}>
            Your advance has been received. Our master tailor is starting work.
          </p>

          <div className="w-full max-w-sm bg-white border p-4 mb-4" style={{ borderColor: LINE }}>
            <div className="flex items-center justify-between py-1.5 border-b" style={{ borderColor: '#F0F0F0' }}>
              <span className="text-[11px]" style={{ color: MUTED }}>Order ID</span>
              <span className="text-[12px] font-medium" style={{ color: INK }}>{orderId}</span>
            </div>
            {totalPrice != null && (
              <>
                <div className="flex items-center justify-between py-1.5 border-b" style={{ borderColor: '#F0F0F0' }}>
                  <span className="text-[11px]" style={{ color: MUTED }}>Total</span>
                  <span className="text-[12px]" style={{ color: INK }}>₹{Number(totalPrice).toLocaleString('en-IN')}</span>
                </div>
                {advanceAmount != null && (
                  <div className="flex items-center justify-between py-1.5 border-b" style={{ borderColor: '#F0F0F0' }}>
                    <span className="text-[11px]" style={{ color: '#166534' }}>Advance Paid ✓</span>
                    <span className="text-[12px]" style={{ color: '#166534', fontWeight: 500 }}>₹{Number(advanceAmount).toLocaleString('en-IN')}</span>
                  </div>
                )}
                {balanceAmount != null && (
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-[11px]" style={{ color: GOLD }}>Balance on delivery</span>
                    <span className="text-[12px]" style={{ color: GOLD, fontWeight: 500 }}>₹{Number(balanceAmount).toLocaleString('en-IN')}</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Notification status */}
          <div className="w-full max-w-sm flex items-center justify-center gap-4 mb-6 text-[10px]">
            <span className="flex items-center gap-1" style={{ color: emailSent ? '#166534' : MUTED }}>
              <Mail className="w-3 h-3" strokeWidth={1.5} />
              {emailSent ? 'Email sent' : notifications?.email?.skipped ? 'Email skipped' : 'Email pending'}
            </span>
            <span className="flex items-center gap-1" style={{ color: smsSent ? '#166534' : MUTED }}>
              <Phone className="w-3 h-3" strokeWidth={1.5} />
              {smsSent ? 'SMS sent' : notifications?.sms?.skipped ? 'SMS skipped' : 'SMS pending'}
            </span>
          </div>

          <button data-testid="go-to-tracking" onClick={() => navigate('/my-orders')}
            className="inline-flex items-center gap-2 px-8 py-3 text-[11px] tracking-[0.1em] uppercase text-white"
            style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
            <Package className="w-4 h-4" strokeWidth={1.5} /> Track My Order <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button onClick={() => navigate('/landing')} className="mt-3 text-[10px] tracking-[0.08em] uppercase" style={{ color: MUTED }}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="payment-result-failure" className="min-h-screen flex flex-col" style={{ backgroundColor: SOFT, fontFamily: 'Outfit, sans-serif' }}>
      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4"
          style={{ backgroundColor: 'rgba(153,27,27,0.08)' }}>
          <XCircle className="w-10 h-10" style={{ color: '#991B1B' }} strokeWidth={1.5} />
        </div>
        <p className="text-[10px] tracking-[0.2em] uppercase mb-2" style={{ color: '#991B1B' }}>Payment Pending</p>
        <h1 className="text-[22px] leading-tight mb-2" style={{ fontFamily: 'Playfair Display, serif', color: INK }}>
          We couldn't confirm your order
        </h1>
        <p className="text-[12px] max-w-sm mb-4" style={{ color: MUTED, lineHeight: 1.5 }}>
          {reason || 'Your payment did not go through. Your order has been saved as pending — you can retry the advance payment anytime.'}
        </p>
        {orderId && (
          <p className="text-[11px] mb-5" style={{ color: INK }}>
            Order ID: <span className="font-medium">{orderId}</span>
          </p>
        )}
        <div className="space-y-2 w-full max-w-sm">
          <button onClick={() => navigate('/my-orders')}
            className="w-full py-3 text-[11px] tracking-[0.08em] uppercase text-white"
            style={{ backgroundColor: INK }}>
            Retry from My Orders
          </button>
          <button onClick={() => navigate('/landing')}
            className="w-full py-3 text-[10px] tracking-[0.08em] uppercase border" style={{ borderColor: LINE, color: MUTED }}>
            Back to Home
          </button>
        </div>
        <div className="mt-6 p-3 flex items-start gap-2 text-left w-full max-w-sm" style={{ backgroundColor: 'rgba(212,175,55,0.06)' }}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: GOLD }} strokeWidth={1.5} />
          <p className="text-[10px]" style={{ color: MUTED, lineHeight: 1.5 }}>
            No money has been deducted if payment didn't complete. Any charge will be refunded within 5-7 business days as per Razorpay policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentResult;
